import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useAdminFixtures, type AdminFixtureRow } from './api';
import { DataTableShell } from './shared';

const ratingClass: Record<number, string> = {
  1: 'bg-emerald-700 text-white',
  2: 'bg-emerald-100 text-emerald-950',
  3: 'bg-amber-100 text-amber-950',
  4: 'bg-red-100 text-red-950',
  5: 'bg-red-800 text-white',
};

function validRating(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 5;
}

/** Read backend enrichment, never request the provider directly or estimate a rating. */
export function WeeklyFdrTable({ season, rounds }: { season: string; rounds: number }) {
  const { data, isLoading, error } = useAdminFixtures({ league: 'EPL', limit: 500, offset: 0 });
  const rows = data?.data ?? [];
  const expectedSeason = season.replace('/', '-');
  const seasonMismatch = rows.some((f) => {
    const fixtureSeason = /^fpl:(\d{4}-\d{2}):fixture:/.exec(f.id)?.[1];
    return !!fixtureSeason && fixtureSeason !== expectedSeason;
  });
  const complete = !!data && data.meta.offset === 0 && rows.length === data.meta.total;
  const teams = [...new Set(rows.flatMap((f) => [f.home_team, f.away_team]))].sort();
  const weeks = Array.from({ length: rounds }, (_, i) => i + 1);
  const rated = rows.filter(
    (f) => validRating(f.home_difficulty) && validRating(f.away_difficulty),
  ).length;
  const missing = rows.length - rated;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Weekly EPL FDR · {season}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <DataTableShell
          loading={isLoading}
          error={error}
          empty={rows.length === 0}
          emptyLabel="No EPL fixtures returned for this dataset; weekly FDR cannot be assessed."
        >
          {seasonMismatch ? (
            <p role="alert" className="text-sm text-destructive">
              Season mismatch: fixture identities do not match EPL {season}. Ratings withheld;
              check the backend dataset selection.
            </p>
          ) : (
            <>
              <p className="text-sm font-medium">{rated} / {rows.length} fixtures rated</p>
              {missing > 0 ? (
                <div className="text-xs text-muted-foreground">
                  <p>{missing} {missing === 1 ? 'fixture' : 'fixtures'} missing one or both FDR values.</p>
                  <p>
                    Check backend FPL feed availability, club matching and season selection;
                    this response does not identify which failed. No estimated ratings are substituted.
                  </p>
                </div>
              ) : null}
              {!complete && data ? (
                <p className="text-sm text-amber-700">
                  Partial fixture coverage: {rows.length} / {data.meta.total} loaded.
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                FPL ratings from backend fixture enrichment: 1 easiest, 5 hardest. H/A is the team’s venue.
                FDR missing means a fixture exists without a valid rating, not a blank gameweek or an automatic postponement.
              </p>
              <div className="relative overflow-x-auto rounded-md border">
                <table className="w-full text-xs">
                  <caption className="sr-only">
                    Team-specific fixture difficulty by gameweek for EPL {season}
                  </caption>
                  <thead>
                    <tr>
                      <th className="p-2 text-left" scope="col">Team</th>
                      {weeks.map((week) => (
                        <th className="min-w-32 p-2 text-left" scope="col" key={week}>GW{week}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team) => (
                      <tr className="border-t" key={team}>
                        <th className="whitespace-nowrap p-2 text-left" scope="row">{team}</th>
                        {weeks.map((week) => {
                          const fixtures = rows.filter(
                            (f) => f.effective_round === week && (f.home_team === team || f.away_team === team),
                          );
                          return (
                            <td className="p-2 align-top" key={week}>
                              {fixtures.length ? fixtures.map((fixture) => (
                                <FixtureCell key={fixture.id} fixture={fixture} team={team} />
                              )) : (
                                <span className="text-muted-foreground">
                                  {complete ? 'No fixture' : 'Not loaded'}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DataTableShell>
      </CardContent>
    </Card>
  );
}

function FixtureCell({ fixture, team }: { fixture: AdminFixtureRow; team: string }) {
  const home = fixture.home_team === team;
  const value = home ? fixture.home_difficulty : fixture.away_difficulty;
  return (
    <div className="mb-1 space-y-1">
      <div>{home ? fixture.away_team : fixture.home_team} ({home ? 'H' : 'A'})</div>
      {['postponed', 'cancelled', 'suspended', 'abandoned'].includes(fixture.status) ? (
        <div className="font-medium text-amber-700">
          {fixture.status[0].toUpperCase() + fixture.status.slice(1)}
        </div>
      ) : null}
      {validRating(value) ? (
        <span
          className={`inline-block rounded px-2 py-0.5 font-medium ${ratingClass[value]}`}
          title="Fantasy Premier League · team-specific difficulty"
        >
          {value}
        </span>
      ) : (
        <span className="text-muted-foreground">FDR missing</span>
      )}
    </div>
  );
}
